/**
 * Analyzer: Day Process Sub-grades (Wave B.1, v7.4.4)
 *
 * SMB Capital / Bellafiore's Daily Report Card framework: decompose the
 * single A–F day grade into per-behavior letters so a "B day" is
 * interpretable. The four sub-grades each measure a distinct discipline
 * dimension — a trader can win money on a day where Sizing is C but
 * Plan Adherence is A, and the breakdown tells them what to work on
 * regardless of the green/red outcome.
 *
 * Sub-grades:
 *
 *   1. SIZING (Bellafiore) — did you risk MORE on high-conviction trades
 *      and LESS on low-conviction? Inverse correlation = F.
 *
 *   2. PATIENCE (Brooks / Shannon / Raschke / Grimes / Diaz) — stalker
 *      vs chaser ratio from Wave B.2's entryDiscipline tag.
 *
 *   3. EXIT (Diaz / SMB) — count of exit-discipline violations from the
 *      existing tradeFlags (gave-back, round-trip, panic-exit).
 *
 *   4. PLAN (Hougaard / Bellafiore) — leverages existing thesisValidation:
 *      what % of trades were on-direction + on-symbol vs the journaled thesis.
 *
 * Each sub-grade returns a 0–100 score → letter A/B/C/D/F via standard
 * cutoffs. Sub-grades that can't be evaluated (no conviction tags, no
 * entryDiscipline, no thesis, etc.) return null and are omitted from
 * the observation; the UI shows "—" for those.
 *
 * Output: a SUCCESS-or-INFO observation whose `evidence.subGrades`
 * carries the structured data. The Live Coach Feed renderer reads
 * `evidence.subGrades` and shows them as a 4-up grid below the overall
 * letter. The detail string narrates the weakest sub-grade as the
 * "drag" for the day.
 *
 * Reads:
 *   day.trades — enriched with .netPnl, .conviction, .quantity, .mistakes,
 *                .confluences, .stopLoss, .barAnalysis.{entryDiscipline,...}
 *   day.journalEntry?.thesis — for plan adherence
 */

import { CATEGORY, SEVERITY, makeObservation } from '../../types.js';
import { computeTradeFlags } from '../../../barAnalysis/tradeFlags.js';

export const key = 'dayProcessSubgrades';
export const tier = 'free';

const MIN_TRADES = 3;

// Cutoffs: score → letter
const scoreToLetter = (score) => {
  if (!Number.isFinite(score)) return null;
  if (score >= 90) return 'A';
  if (score >= 80) return 'B';
  if (score >= 70) return 'C';
  if (score >= 60) return 'D';
  return 'F';
};

const fmtPct = (n) => `${Math.round(n * 100)}%`;
const fmtScore = (s) => Number.isFinite(s) ? `${s.toFixed(0)}/100` : '—';

// ── Sub-grade: SIZING ────────────────────────────────────────────
// Compare avg quantity on conviction-4/5 trades vs conviction-1/2.
// Returns { score, letter, detail, evidence } or null when not enough
// conviction-tagged trades to evaluate.

const computeSizingSubgrade = (trades) => {
  const high = trades.filter(t => {
    const c = parseFloat(t.conviction);
    return Number.isFinite(c) && c >= 4;
  });
  const low = trades.filter(t => {
    const c = parseFloat(t.conviction);
    return Number.isFinite(c) && c <= 2 && c >= 1;
  });
  if (high.length < 2 || low.length < 2) return null;

  const avgQty = (arr) => arr.reduce((s, t) => s + (parseInt(t.quantity) || 0), 0) / arr.length;
  const highQty = avgQty(high);
  const lowQty  = avgQty(low);
  if (highQty <= 0 || lowQty <= 0) return null;

  // Ratio of high-conviction size to low-conviction size. > 1 = good
  // (sized up on conviction). < 1 = inverted.
  const ratio = highQty / lowQty;

  // Score: 100 when ratio ≥ 2 (you risked 2x on high-conv); 50 when ratio = 1
  // (no scaling); 0 when ratio ≤ 0.5 (you sized DOWN on high conviction).
  // Linear in between, clamped.
  let score;
  if (ratio >= 2) score = 100;
  else if (ratio <= 0.5) score = 0;
  else if (ratio >= 1) score = 50 + (ratio - 1) * 50;
  else score = (ratio - 0.5) * 100;

  return {
    score,
    letter: scoreToLetter(score),
    detail: `High-conviction (4-5) trades: avg ${highQty.toFixed(1)} contracts. Low-conviction (1-2): avg ${lowQty.toFixed(1)}. Ratio ${ratio.toFixed(2)}× (target ≥ 2×).`,
    evidence: { highCount: high.length, lowCount: low.length, highQty, lowQty, ratio },
  };
};

// ── Sub-grade: PATIENCE ──────────────────────────────────────────
// Stalker vs chaser ratio from entryDiscipline tags.

const computePatienceSubgrade = (trades) => {
  const tagged = trades.filter(t => t?.barAnalysis?.entryDiscipline?.discipline);
  if (tagged.length < 3) return null;
  const stalkers = tagged.filter(t => t.barAnalysis.entryDiscipline.discipline === 'stalker').length;
  const chasers  = tagged.filter(t => t.barAnalysis.entryDiscipline.discipline === 'chaser').length;
  const total = tagged.length;

  // Score = (stalker share) − (chaser share) × 100, mapped to 0-100.
  // All stalker (no chaser) → 100. Half-half → 50. All chaser → 0.
  const stalkerShare = stalkers / total;
  const chaserShare  = chasers / total;
  const raw = stalkerShare - chaserShare;  // -1..+1
  const score = Math.max(0, Math.min(100, (raw + 1) * 50));

  return {
    score,
    letter: scoreToLetter(score),
    detail: `${stalkers} stalker · ${chasers} chaser · ${total - stalkers - chasers} neutral entries.`,
    evidence: { stalkers, chasers, total, stalkerShare, chaserShare },
  };
};

// ── Sub-grade: EXIT DISCIPLINE ──────────────────────────────────
// Count of exit-violation flags per trade.

const EXIT_VIOLATION_FLAGS = new Set([
  'panic-exit',       // bailed before invalidation (Wave A)
  'mfe-leak',         // captured < 30% of move
  'round-trip',       // hit big MFE but kept little
  'bag-held',         // crossed half-target then lost
]);

const computeExitSubgrade = (trades) => {
  if (trades.length < MIN_TRADES) return null;
  let violations = 0;
  let evaluated = 0;
  for (const t of trades) {
    const flags = computeTradeFlags(t);
    if (flags.length === 0) continue;  // no barAnalysis = unevaluable for exit chips
    evaluated++;
    if (flags.some(f => EXIT_VIOLATION_FLAGS.has(f.key))) violations++;
  }
  if (evaluated < MIN_TRADES) return null;

  // Score: 100 when 0 violations, drops by 20 per violation, floor 0.
  const score = Math.max(0, 100 - (violations / evaluated) * 100);

  return {
    score,
    letter: scoreToLetter(score),
    detail: `${violations} exit-violation flag${violations === 1 ? '' : 's'} across ${evaluated} bar-analyzed trades (panic exits, MFE leaks, round-trips, bag-helds).`,
    evidence: { violations, evaluated },
  };
};

// ── Sub-grade: PLAN ADHERENCE ────────────────────────────────────
// Uses existing thesis-text matching: direction + symbol match between
// thesis and actual trades.

const LONG_WORDS = /\b(long|buy|bought|bid|bull|bullish|dip|support|reclaim|breakout\s+long|pullback\s+buy)\b/i;
const SHORT_WORDS = /\b(short|sell|sold|offer|bear|bearish|fade|reject|breakdown|lower\s+high|supply)\b/i;

const extractIntent = (text) => {
  if (!text || typeof text !== 'string') return null;
  const long = LONG_WORDS.test(text);
  const short = SHORT_WORDS.test(text);
  if (long && short) return 'either';
  if (long) return 'long';
  if (short) return 'short';
  return null;
};

const computePlanSubgrade = (trades, journalEntry) => {
  if (trades.length < MIN_TRADES) return null;
  const thesis = journalEntry?.thesis;
  const intent = extractIntent(thesis);
  if (!intent || intent === 'either') return null;  // no clear plan to grade against

  const onPlan = trades.filter(t => {
    const dir = String(t.direction || '').toLowerCase();
    return dir === intent;
  }).length;
  const ratio = onPlan / trades.length;
  const score = Math.round(ratio * 100);

  return {
    score,
    letter: scoreToLetter(score),
    detail: `Thesis intent: ${intent}. ${onPlan} of ${trades.length} trades matched (${fmtPct(ratio)}).`,
    evidence: { intent, onPlan, total: trades.length, ratio },
  };
};

// ── Public analyze() ────────────────────────────────────────────

export const analyze = (ctx) => {
  const { day } = ctx || {};
  if (!day?.trades || !Array.isArray(day.trades)) return null;
  if (day.trades.length < MIN_TRADES) return null;

  const trades = day.trades;
  const subGrades = {
    sizing:   computeSizingSubgrade(trades),
    patience: computePatienceSubgrade(trades),
    exit:     computeExitSubgrade(trades),
    plan:     computePlanSubgrade(trades, day.journalEntry),
  };

  // Bail if EVERY sub-grade is null — nothing meaningful to surface.
  const evaluated = Object.entries(subGrades).filter(([, v]) => v !== null);
  if (evaluated.length === 0) return null;

  // Find the weakest sub-grade for the "drag" narrative.
  const weakest = evaluated.reduce((acc, [name, sg]) => {
    if (!acc || sg.score < acc.sg.score) return { name, sg };
    return acc;
  }, null);

  // Find the strongest for positive framing
  const strongest = evaluated.reduce((acc, [name, sg]) => {
    if (!acc || sg.score > acc.sg.score) return { name, sg };
    return acc;
  }, null);

  // Severity: warn if any sub-grade is D/F; info otherwise; success if all A/B.
  const allHighGrades = evaluated.every(([, v]) => v.score >= 80);
  const anyLowGrades  = evaluated.some(([, v]) => v.score < 70);
  const severity = allHighGrades ? SEVERITY.SUCCESS
                 : anyLowGrades  ? SEVERITY.WARNING
                                 : SEVERITY.INFO;

  // Headline narrates the spread
  const summaryParts = evaluated.map(([name, v]) => `${name[0].toUpperCase()}${name.slice(1)} ${v.letter}`);
  const title = `Process sub-grades: ${summaryParts.join(' · ')}`;

  const detail = anyLowGrades
    ? `Weakest: ${weakest.name} at ${fmtScore(weakest.sg.score)} — ${weakest.sg.detail}`
    : `Strongest: ${strongest.name} at ${fmtScore(strongest.sg.score)} — ${strongest.sg.detail}`;

  const action = anyLowGrades
    ? `Pick ${weakest.name} as your one focus for next session. The other sub-grades show you have the discipline elsewhere; this is the leverage point.`
    : 'Sub-grades are tight across the board. Maintain whatever pre-session routine you used today.';

  return makeObservation({
    id: `${key}:${day.date}`,
    analyzer: key,
    scope: 'day',
    scopeId: day.date,
    severity,
    category: CATEGORY.PROCESS,
    title,
    detail,
    action,
    evidence: {
      subGrades,
      weakest: weakest ? { name: weakest.name, ...weakest.sg } : null,
      strongest: strongest ? { name: strongest.name, ...strongest.sg } : null,
      evaluatedCount: evaluated.length,
    },
    confidence: 0.85,
    priority: 68,
  });
};

// Exports for tests
export {
  computeSizingSubgrade,
  computePatienceSubgrade,
  computeExitSubgrade,
  computePlanSubgrade,
  scoreToLetter,
};
